/**
 * Admin base service factory.
 *
 * Similar to `createBaseService` but designed for system-admin operations:
 * - Does NOT auto-inject `tenant_id` on create (admin creates cross-tenant records)
 * - Does NOT auto-inject `created_by`/`updated_by` (admin services manage this differently)
 * - Keeps same getAll/get/create/update/delete CRUD pattern
 */

import { getSupabase as getSupabaseNullable } from "../../../lib/supabase";
import type { IOperationResult, IGetAllOptions } from "../../types";
import { toDb, fromDb } from "../baseService";

function requireSupabase() {
  const client = getSupabaseNullable();
  if (!client) throw new Error("Supabase not initialized. Call initSupabase() first.");
  return client;
}

function toSnakeCase(str: string): string {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}

export interface AdminBaseServiceOptions {
  /** The actual Postgres table name. */
  tableName: string;
  /** Column names (snake_case) to search with ilike when `options.search` is provided. */
  searchColumns?: string[];
  /** camelCase field names that exist in the view but not the table. Stripped before insert/update. */
  computedColumns?: string[];
}

export function createAdminBaseService<
  T extends { id: string },
  TBase extends Record<string, unknown>,
>(options: AdminBaseServiceOptions) {
  const { tableName, searchColumns = [], computedColumns = [] } = options;

  function stripComputed(obj: Record<string, unknown>): Record<string, unknown> {
    if (computedColumns.length === 0) return obj;
    const clean = { ...obj };
    for (const col of computedColumns) {
      delete clean[col];
    }
    return clean;
  }

  return {
    async getAll(
      queryOptions?: IGetAllOptions
    ): Promise<IOperationResult<T[]>> {
      try {
        const supabase = requireSupabase();
        let query = supabase
          .from(tableName)
          .select("*", { count: "exact" });

        // Search
        if (queryOptions?.search && searchColumns.length > 0) {
          const term = `%${queryOptions.search}%`;
          const orClause = searchColumns
            .map((col) => `${col}.ilike.${term}`)
            .join(",");
          query = query.or(orClause);
        }

        // Sorting
        if (queryOptions?.orderBy?.length) {
          for (const orderSpec of queryOptions.orderBy) {
            const [field, dir = "asc"] = orderSpec.split(" ");
            query = query.order(toSnakeCase(field), {
              ascending: dir === "asc",
            });
          }
        }

        // Pagination
        if (queryOptions?.skip != null && queryOptions?.top != null) {
          query = query.range(
            queryOptions.skip,
            queryOptions.skip + queryOptions.top - 1
          );
        } else if (queryOptions?.top != null) {
          query = query.limit(queryOptions.top);
        }

        const { data, error, count } = await query;
        if (error) return { success: false, error };

        const mapped = (data ?? []).map((row) =>
          fromDb<T>(row as Record<string, unknown>)
        );
        return {
          success: true,
          data: mapped,
          totalCount: count ?? undefined,
        };
      } catch (error) {
        return { success: false, error };
      }
    },

    async get(id: string): Promise<IOperationResult<T>> {
      try {
        const supabase = requireSupabase();
        const { data, error } = await supabase
          .from(tableName)
          .select("*")
          .eq("id", id)
          .single();

        if (error) return { success: false, error };
        return {
          success: true,
          data: fromDb<T>(data as Record<string, unknown>),
        };
      } catch (error) {
        return { success: false, error };
      }
    },

    async create(record: TBase): Promise<IOperationResult<T>> {
      try {
        const supabase = requireSupabase();
        const dbRecord = toDb(stripComputed(record as Record<string, unknown>));

        const { data, error } = await supabase
          .from(tableName)
          .insert(dbRecord)
          .select()
          .single();

        if (error) return { success: false, error };
        return {
          success: true,
          data: fromDb<T>(data as Record<string, unknown>),
        };
      } catch (error) {
        return { success: false, error };
      }
    },

    async update(
      id: string,
      fields: Partial<TBase>
    ): Promise<IOperationResult<T>> {
      try {
        const supabase = requireSupabase();
        const dbFields = toDb(stripComputed(fields as Record<string, unknown>));

        const { data, error } = await supabase
          .from(tableName)
          .update(dbFields)
          .eq("id", id)
          .select()
          .single();

        if (error) return { success: false, error };
        return {
          success: true,
          data: fromDb<T>(data as Record<string, unknown>),
        };
      } catch (error) {
        return { success: false, error };
      }
    },

    async delete(id: string): Promise<IOperationResult<void>> {
      try {
        const supabase = requireSupabase();
        const { error } = await supabase.from(tableName).delete().eq("id", id);
        if (error) return { success: false, error };
        return { success: true };
      } catch (error) {
        return { success: false, error };
      }
    },
  };
}
